import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeMode } from '@/shared/domain/types';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { setThemeMode } from '@/shared/store/slices/themeSlice';
import {
  applyThemeToDocument,
  darkMediaQuery,
  resolveTheme,
  systemPrefersDark,
  ThemeContext,
  writeStoredTheme,
  type ThemeContextValue,
} from './themeContext';

/**
 * Aplica el tema elegido (Redux: `theme.mode`) al documento y lo guarda en `pizarra:theme` para que
 * `public/theme-init.js` lo use antes de pintar. En modo 'system' sigue `prefers-color-scheme`.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const mode = useAppSelector((s) => s.theme.mode);
  const dispatch = useAppDispatch();
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const query = darkMediaQuery();
    if (!query) return undefined;
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    setSystemDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved = resolveTheme(mode, systemDark);

  useLayoutEffect(() => {
    applyThemeToDocument(resolved);
  }, [resolved]);

  useEffect(() => {
    writeStoredTheme(mode);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => dispatch(setThemeMode(next)), [dispatch]);
  const toggle = useCallback(
    () => setMode(resolved === 'dark' ? 'light' : 'dark'),
    [resolved, setMode],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode, toggle }),
    [mode, resolved, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
